const moment = require('moment');
const { groupBy, merge } = require('lodash');

function stringify(...args) {
  const { stringify: csvStringify } = require('csv-stringify/sync');
  return csvStringify(...args);
}

export function toNumber(value) {
  const numValue = parseInt(value, 10);
  return Number.isFinite(numValue) ? numValue : 0;
}

// assembles asn and site paths used for grouping
// and returns asn details for color schemes and legends
export function getGroupPathFromProbe(probe, { uniqueValues = true } = {}) {
  const { asNumbers, sites, asns } = (probe?.hops || []).reduce(
    (acc, hop) => {
      const { asn, site } = hop;

      if (asn) {
        acc.asNumbers.push(+asn.id);
        acc.asns[asn.id] = asn;
      }

      if (site) {
        acc.sites.push(+site.id);
      }

      return acc;
    },
    { asNumbers: [], sites: [], asns: {} }
  );

  let asPath = asNumbers;
  let sitePath = sites;

  if (uniqueValues) {
    asPath = [...new Set(asPath)];
    sitePath = [...new Set(sitePath)];
  }

  return { asPath, sitePath, asns };
}

// used as part of the backend to api transformation
// helps assemble reference data of nodes as well as asn path and ip/latency hops
function getProbeDetails(probe) {
  const probeGroupPaths = getGroupPathFromProbe(probe, { uniqueValues: false });

  return (probe?.hops || []).reduce(
    (acc, hop) => {
      const { ip, asn, geo, site, dns, latency, ingress_interface, egress_interface } = hop;

      if (ip && !acc.nodes[ip]) {
        acc.nodes[ip] = {
          ip,
          asn: asn?.id ? +asn.id : null,
          as_name: asn?.name,
          location: {
            country: geo?.country?.name,
            region: geo?.region?.name || '-',
            city: geo?.city?.name || '-',
            latitude: geo?.city?.latitude,
            longitude: geo?.city?.longitude
          },
          dns_name: dns?.hostname,
          site_id: site?.id,
          device_id: (ingress_interface || egress_interface || {}).device_id
        };
      }

      acc.hops.push({ node_id: ip || '', latency: +latency });

      acc.as_path = probeGroupPaths.asPath;

      return acc;
    },
    { hops: [], as_path: [], nodes: {} }
  );
}

function getProbes(probes) {
  return (probes || []).reduce(
    (acc, probe) => {
      const { completed: is_complete } = probe;
      const { hops, as_path, nodes } = getProbeDetails(probe);

      acc.traces.push({
        as_path,
        hops,
        is_complete
      });

      acc.max_as_path_length = Math.max(acc.max_as_path_length, as_path.length);

      acc.nodes = {
        ...acc.nodes,
        ...nodes
      };

      return acc;
    },
    { traces: [], max_as_path_length: 0, nodes: {} }
  );
}

export function backendToAPIResponse(response) {
  const { trace_time_series } = response || {};

  return Object.entries(trace_time_series || {}).reduce(
    (acc, [tsKey, tsData]) => {
      Object.values(tsData.tasks || {}).forEach((taskData) => {
        Object.entries(taskData.agents || {}).forEach(([agentId, agentData]) => {
          const { target, hop_count, probes } = agentData;
          const totalHopCount = hop_count?.total;
          const { traces, max_as_path_length, nodes } = getProbes(probes);
          const path = {
            agent_id: agentId,
            target_ip: target?.target_ip,
            hop_count: {
              average: totalHopCount,
              min: totalHopCount,
              max: totalHopCount
            },
            time: {
              seconds: tsKey,
              nanos: 0
            },
            traces,
            max_as_path_length
          };

          // add the new path and merge in discovered nodes
          acc.paths.push(path);
          acc.nodes = {
            ...acc.nodes,
            ...nodes
          };
        });
      });

      return acc;
    },
    { paths: [], nodes: {} }
  );
}

// used in preview tests, rolls up results into the last timestamp since results can sprinkle in across multiple timestamps
export function rollupTraceTimeSeries(response) {
  return Object.entries(response?.trace_time_series || {}).reduce((acc, [, tsData], i, allSeries) => {
    // roll up all the time series data into the last timestamp
    const [lastTsKey] = allSeries[allSeries.length - 1];
    acc.trace_time_series[lastTsKey] = merge(acc.trace_time_series[lastTsKey], tsData);

    return acc;
  }, response);
}

/*
  Given a task map of trace data, optionally filters the results by agent id(s) and ip(s).
  Example of relevant data passed in:

  {
    [TASKID]: {
      agents: {
        [AGENTID]: { target_ip: [TARGETIP] },
        // more agents...
      }
    },
    // more tasks...
  }
*/
function filterTasks({ tasks = {}, selectedAgentIds = [], selectedIps = [] } = {}) {
  if (selectedAgentIds.length || selectedIps.length) {
    return Object.entries(tasks).reduce((taskAcc, [taskId, task]) => {
      const filteredAgents = Object.entries(task.agents).reduce((agentAcc, [agentId, agentTrace]) => {
        // assume that filtering lists weren't provided for one of agent or ip
        let matchesAgent = true;
        let matchesIp = true;

        if (selectedAgentIds.length) {
          // we have agent ids to filter by
          matchesAgent = selectedAgentIds.indexOf(agentId) > -1;
        }

        if (selectedIps.length) {
          // we have ips to filter by
          matchesIp = selectedIps.indexOf(agentTrace.target.target_ip) > -1;
        }

        if (matchesAgent && matchesIp) {
          // agent and target needs to both match to keep the trace results in the task map
          agentAcc[agentId] = agentTrace;
        }

        return agentAcc;
      }, {});

      taskAcc[taskId] = { agents: filteredAgents };

      return taskAcc;
    }, {});
  }

  return tasks;
}

/*
  Returns data necessary to hydrate the Path View panel in test results.
  The output can be optionally filtered by agent ids, necessary for handling changes
  in the path hop graph based on filter form selections.
*/
export function getTraceroutes({ selectedAgentIds, selectedIps, traceResults = {} }) {
  const traceroutes = [];
  const { trace_time_series = {} } = traceResults || {};
  const tsKeys = Object.keys(trace_time_series);

  for (let i = 0; i < tsKeys.length; i += 1) {
    const ts = +tsKeys[i];
    const { tasks, distance_stats, hop_stats } = trace_time_series[ts];
    const filteredTasks = filterTasks({ tasks, selectedAgentIds, selectedIps });
    const taskKeys = Object.keys(filteredTasks);
    const traceroute = {
      distance_stats,
      hopCount: hop_stats.sum_of_hops_for_all_agents,
      time: +ts * 1000,
      actualHopCount: 0,
      actualDistance: 0,
      asns: {}, // asn id/name reference data
      agents: {}, // hash lookup of historically accurate agent data
      traces: [] // overwritten below when the time series entry has tasks
    };
    const traces = [];

    for (let j = 0; j < taskKeys.length; j += 1) {
      const taskId = +taskKeys[j];
      const { agents } = filteredTasks[taskId];
      const agentKeys = Object.keys(agents);

      for (let k = 0; k < agentKeys.length; k += 1) {
        const agentId = +agentKeys[k];
        const agent = agents[agentId];
        const trace = {
          agentId,
          agent_ip: '::',
          hopCount: agent.hop_count.total,
          probes: agent.probes.map((probe) => {
            const { asns, ...groups } = getGroupPathFromProbe(probe);

            // compile a collection of all the asns in a traceroute across all probes
            // this is used as a reference lookup when constructing the traceroute legend and color scheme
            traceroute.asns = { ...traceroute.asns, ...asns };

            return {
              ...probe,
              ...groups
            };
          }),
          target: agent.target,
          time: +ts
        };

        traces.push(trace);

        // agent.hop_count.total is the total hop count across all probes
        // this accumulates hop count and distance using the average value of a single probe from an agent
        traceroute.actualHopCount += agent.hop_count.total;
        traceroute.actualDistance += agent.distance.average_distance_per_probe;

        traceroute.mtu = agent.mtu;

        if (agent?.src_agent) {
          traceroute.agents[agent.src_agent.id] = agent.src_agent;
        }
      }

      traceroute.traces = traces;
    }

    traceroutes.push(traceroute);
  }

  return traceroutes;
}

export function getTraceDataByTarget(traces) {
  return Object.entries(groupBy(traces, 'target.target_ip')).map(([target_ip, targetTraces]) => {
    const targetTrace = targetTraces[0];
    const traceData = { target_ip, trace: [], timestamp: moment.unix(targetTrace.time).toISOString() };

    traceData.trace = targetTrace.probes.reduce((acc, curr) => {
      curr.hops.forEach((hop, hopIndex) => {
        if (!acc[hopIndex]) {
          acc.push({ time: targetTrace.time, ttl: hopIndex, timeouts: 0, hops: [] });
        }

        if (hop.ip) {
          acc[hopIndex].hops.push(hop);
        }

        if (hop.timeout) {
          acc[hopIndex].timeouts += 1;
        }
      });

      return acc;
    }, []);

    return traceData;
  });
}

export function getTraceCsv(traces) {
  const groupedTraces = (traces || []).map((trace) => getTraceDataByTarget([trace])).flat();
  const { data, columns } = groupedTraces.reduce(
    (acc, trace) => {
      (trace.trace || []).forEach(({ ttl, timeouts, hops, time }) => {
        const row = { timestamp: moment.unix(time).toISOString(), hop: ttl };

        [].concat(Array(timeouts).fill({}), hops).forEach(({ ip, latency }, index) => {
          const columnName = `RTT ${index}`;
          row.ip_address = ip || '*';
          row[columnName] = latency ? `${(latency / 1000).toFixed(3)}ms` : '*';
          acc.columns.add(columnName);
        });

        acc.data.push(row);
      });

      return acc;
    },
    { data: [], columns: new Set(['timestamp', 'hop', 'ip_address']) }
  );

  return stringify(data, { header: true, columns: Array.from(columns) });
}
